/**
 * World Space Debug Visualization (Collage View)
 * 
 * Shows all frames positioned in world space with viewport overlay.
 * This helps diagnose spatial positioning and navigation issues.
 * 
 * Press 'D' to toggle this view.
 */

import { useEffect, useRef } from 'react'
import { useAppStore } from '@/store/appStore'
import { getWorldBounds } from '@/utils/worldSpace'

export function WorldSpaceDebug() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  
  // Use individual selectors - extract primitives to ensure re-renders on changes
  const frameWorldPositions = useAppStore(state => state.frameWorldPositions)
  const viewportX = useAppStore(state => state.viewportWorldPosition.x)
  const viewportY = useAppStore(state => state.viewportWorldPosition.y)
  const viewportWidth = useAppStore(state => state.viewportSize.width)
  const viewportHeight = useAppStore(state => state.viewportSize.height)
  const viewportVersion = useAppStore(state => state.viewportVersion)
  const currentCameraIndex = useAppStore(state => state.currentCameraIndex)
  const visibleCameraIndices = useAppStore(state => state.visibleCameraIndices)
  
  // Reconstruct objects
  const viewportWorldPosition = { x: viewportX, y: viewportY }
  const viewportSize = { width: viewportWidth, height: viewportHeight }
  
  // DEBUG: Log state on every render
  console.log('🗺️ [WorldDebug] Render triggered:', {
    frameWorldPositionsLength: frameWorldPositions?.length || 0,
    viewportX,
    viewportY,
    viewportVersion,
    viewportSize,
    currentCameraIndex,
    visibleIndicesCount: visibleCameraIndices?.length || 0,
    timestamp: Date.now()
  })
  
  useEffect(() => {
    console.log('🎨 [WorldDebug] useEffect triggered - redrawing canvas', {
      viewportX,
      viewportY,
      currentFrame: currentCameraIndex,
      timestamp: Date.now()
    })
    
    const canvas = canvasRef.current
    if (!canvas || frameWorldPositions.length === 0) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    // Canvas size
    const width = canvas.width
    const height = canvas.height
    
    // Clear canvas
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(0, 0, width, height)
    
    // Calculate world bounds
    const bounds = getWorldBounds(frameWorldPositions, viewportSize.width, viewportSize.height)
    const worldWidth = bounds.maxX - bounds.minX
    const worldHeight = bounds.maxY - bounds.minY
    
    // Add padding
    const padding = 50
    const availableWidth = width - padding * 2
    const availableHeight = height - padding * 2
    
    // Calculate scale to fit world in canvas
    const scaleX = availableWidth / worldWidth
    const scaleY = availableHeight / worldHeight
    const scale = Math.min(scaleX, scaleY, 1) // Don't scale up, only down
    
    // Transform functions: world coords → canvas coords
    const worldToCanvasX = (worldX: number) => {
      return padding + (worldX - bounds.minX) * scale
    }
    
    const worldToCanvasY = (worldY: number) => {
      return padding + (worldY - bounds.minY) * scale
    }
    
    // Draw grid
    ctx.strokeStyle = '#333'
    ctx.lineWidth = 1
    const gridSpacing = 100 // 100px in world space
    
    for (let x = Math.floor(bounds.minX / gridSpacing) * gridSpacing; x <= bounds.maxX; x += gridSpacing) {
      const canvasX = worldToCanvasX(x)
      ctx.beginPath()
      ctx.moveTo(canvasX, padding)
      ctx.lineTo(canvasX, height - padding)
      ctx.stroke()
    }
    
    for (let y = Math.floor(bounds.minY / gridSpacing) * gridSpacing; y <= bounds.maxY; y += gridSpacing) {
      const canvasY = worldToCanvasY(y)
      ctx.beginPath()
      ctx.moveTo(padding, canvasY)
      ctx.lineTo(width - padding, canvasY)
      ctx.stroke()
    }
    
    // Draw all frames
    frameWorldPositions.forEach((framePos, index) => {
      const x = worldToCanvasX(framePos.worldX)
      const y = worldToCanvasY(framePos.worldY)
      const w = viewportSize.width * scale
      const h = viewportSize.height * scale
      
      // Frame color based on status
      let strokeColor = '#444' // Default: gray
      let fillColor = 'rgba(100, 100, 100, 0.1)'
      let lineWidth = 1
      
      if (index === currentCameraIndex) {
        strokeColor = '#00ff00' // Current: bright green
        fillColor = 'rgba(0, 255, 0, 0.2)'
        lineWidth = 3
      } else if (visibleCameraIndices.includes(index)) {
        strokeColor = '#ffff00' // Visible: yellow
        fillColor = 'rgba(255, 255, 0, 0.15)'
        lineWidth = 2
      }
      
      // Draw frame rectangle
      ctx.strokeStyle = strokeColor
      ctx.fillStyle = fillColor
      ctx.lineWidth = lineWidth
      ctx.strokeRect(x, y, w, h)
      ctx.fillRect(x, y, w, h)
      
      // Draw frame number
      ctx.fillStyle = strokeColor
      ctx.font = '12px monospace'
      ctx.fillText(`${index}`, x + 5, y + 15)
    })
    
    // Draw viewport
    const vpX = worldToCanvasX(viewportWorldPosition.x)
    const vpY = worldToCanvasY(viewportWorldPosition.y)
    const vpW = viewportSize.width * scale
    const vpH = viewportSize.height * scale
    
    ctx.strokeStyle = '#ff0000' // Red for viewport
    ctx.lineWidth = 3
    ctx.setLineDash([10, 5])
    ctx.strokeRect(vpX, vpY, vpW, vpH)
    ctx.setLineDash([])
    
    // Draw crosshair at viewport center
    const vpCenterX = vpX + vpW / 2
    const vpCenterY = vpY + vpH / 2
    const crossSize = 20
    
    ctx.strokeStyle = '#ff0000'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(vpCenterX - crossSize, vpCenterY)
    ctx.lineTo(vpCenterX + crossSize, vpCenterY)
    ctx.moveTo(vpCenterX, vpCenterY - crossSize)
    ctx.lineTo(vpCenterX, vpCenterY + crossSize)
    ctx.stroke()
    
    // Draw info panel
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
    ctx.fillRect(10, 10, 300, 180)
    
    ctx.fillStyle = '#fff'
    ctx.font = '14px monospace'
    let infoY = 30
    const lineHeight = 20
    
    ctx.fillText('🌍 WORLD SPACE DEBUG', 20, infoY)
    infoY += lineHeight * 1.5
    
    ctx.fillText(`Frames: ${frameWorldPositions.length}`, 20, infoY)
    infoY += lineHeight
    
    ctx.fillText(`World Size: ${worldWidth.toFixed(0)}×${worldHeight.toFixed(0)}px`, 20, infoY)
    infoY += lineHeight
    
    ctx.fillText(`Viewport: (${viewportWorldPosition.x.toFixed(0)}, ${viewportWorldPosition.y.toFixed(0)})`, 20, infoY)
    infoY += lineHeight
    
    ctx.fillText(`Current Frame: #${currentCameraIndex}`, 20, infoY)
    infoY += lineHeight
    
    ctx.fillText(`Visible Frames: [${visibleCameraIndices.join(', ')}]`, 20, infoY)
    infoY += lineHeight
    
    ctx.fillText(`Scale: ${(scale * 100).toFixed(1)}%`, 20, infoY)
    infoY += lineHeight * 1.5
    
    ctx.fillStyle = '#00ff00'
    ctx.fillText('█ Current', 20, infoY)
    ctx.fillStyle = '#ffff00'
    ctx.fillText('█ Visible', 110, infoY)
    ctx.fillStyle = '#ff0000'
    ctx.fillText('▬ Viewport', 200, infoY)
    
  }, [frameWorldPositions, viewportX, viewportY, viewportWidth, viewportHeight, viewportVersion, currentCameraIndex, visibleCameraIndices])
  
  if (frameWorldPositions.length === 0) {
    console.log('❌ [WorldDebug] Showing loading screen - no frame positions')
    console.log('   - frameWorldPositions:', frameWorldPositions)
    console.log('   - Length:', frameWorldPositions?.length)
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        fontFamily: 'monospace',
        fontSize: '20px'
      }}>
        <div>
          <p>⏳ Waiting for world positions to be calculated...</p>
          <p style={{ fontSize: '14px', marginTop: '10px', opacity: 0.7 }}>
            Load a dataset with offset map to see world space visualization
          </p>
        </div>
      </div>
    )
  }
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.95)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        padding: '20px',
        color: 'white',
        fontFamily: 'monospace',
        fontSize: '14px',
        borderBottom: '1px solid #333'
      }}>
        <strong>World Space Debug (Collage View)</strong> - Press 'D' to close
      </div>
      
      <canvas 
        ref={canvasRef} 
        width={1600} 
        height={900}
        style={{
          flex: 1,
          width: '100%',
          height: '100%',
          objectFit: 'contain'
        }}
      />
      
      <div style={{
        padding: '10px 20px',
        color: '#888',
        fontFamily: 'monospace',
        fontSize: '12px',
        borderTop: '1px solid #333'
      }}>
        Tip: Use console commands to test viewport positioning: 
        <code style={{ marginLeft: '10px', color: '#0ff' }}>
          useAppStore.getState().setViewportPosition(500, 100)
        </code>
      </div>
    </div>
  )
}
